export interface SelectedServer {
  id: string
  name: string
  url: string
}

// Discriminated union for the checkbox value payload.
// Matches the three shapes emitted by _connection_methods.html.erb.
export type EntryValue =
  | {type: 'remote'; display_name: string; transport_id: number}
  | {type: 'remote'; display_name: string; remote_url: string; remote_transport_type?: string}
  | {type: 'package'; display_name: string; package_id: number}

export interface ServerEntry {
  id: string // unique key derived from the checkbox value
  type: 'remote' | 'package'
  displayName: string
  selected: boolean
  value: EntryValue // original parsed checkbox value for form serialization
}

export interface SelectedOrganization {
  id: string
  name: string
}

export const PANEL_IDS = {
  EXTERNAL: 'panel-test-external',
  GITHUB: 'panel-test-github',
} as const

export const SELECTORS = {
  DIALOG: '#create-allowlist-dialog',
  CONTROLLER: '[data-controller="allowlist-dialog"]',
} as const

// --- MCP Server Publication Schema Types ---
// Mirrors https://static.modelcontextprotocol.io/schemas/2025-12-11/server.schema.json

export interface StdioTransport {
  type: 'stdio'
}

export interface StreamableHttpTransport {
  type: 'streamable-http'
  url: string
  headers?: ServerKeyValueInput[]
}

export interface SseTransport {
  type: 'sse'
  url: string
  headers?: ServerKeyValueInput[]
}

export type ServerTransport = StdioTransport | StreamableHttpTransport | SseTransport

export interface ServerKeyValueInput {
  name: string
  description?: string
  isRequired?: boolean
  value?: string
  isSecret?: boolean
  default?: string
  variables?: Record<string, ServerInput>
}

export interface ServerInput {
  description?: string
  isRequired?: boolean
  format?: 'string' | 'number' | 'boolean' | 'filepath'
  value?: string
  isSecret?: boolean
  default?: string
  choices?: string[]
}

export interface ServerArgument {
  type: 'positional' | 'named'
  name?: string
  description?: string
  isRequired?: boolean
  value?: string
  valueHint?: string
  isRepeated?: boolean
  variables?: Record<string, ServerInput>
}

export interface ServerPackage {
  registryType: string
  identifier: string
  version?: string
  transport: ServerTransport
  registryBaseUrl?: string
  runtimeHint?: string
  fileSha256?: string
  runtimeArguments?: ServerArgument[]
  packageArguments?: ServerArgument[]
  environmentVariables?: ServerKeyValueInput[]
}

export interface ServerRepository {
  url: string
  source: string
  id?: string
  subfolder?: string
}

export interface ServerDetail {
  $schema?: string
  name: string
  title?: string
  description: string
  version: string
  websiteUrl?: string
  repository?: ServerRepository
  packages?: ServerPackage[]
  remotes?: Array<StreamableHttpTransport | SseTransport>
  icons?: Array<{src: string; sizes?: string[]; theme?: 'light' | 'dark'; mimeType?: string}>
  _meta?: Record<string, unknown>
  [key: string]: unknown
}

export interface ServerDetailValidationError {
  path: string
  message: string
}

export interface ServerDetailValidationResult {
  valid: boolean
  errors: ServerDetailValidationError[]
}
